import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import ResultsTable from "./table/ResultsTable";
import { getSupabase } from "./supabaseClient";
import { bucketName_files, supabaseUrl } from "./env";

/**
 * Global host for the View Reports modal triggered by the floating Reports
 * bubble. Mirrors PatientPage's modal flow without requiring the user to
 * navigate to a patient page: it loads the canonical reports folder for the
 * last visited row and mounts a `ResultsTable` in `globalModalOnly` mode so
 * the same modal UI (and all of its internal modals) are reused.
 *
 * Listens for:
 *  - `patientReportsContext` (broadcast by PatientPage when a row hydrates)
 *    to keep track of the last folder/patient.
 *  - `globalOpenReportsModal` (dispatched by the bubble) to open the modal in
 *    place. We only handle this when the user is NOT currently on the matching
 *    patient page; that case is handled locally by PatientPage so its richer
 *    folder-resolution logic is preserved.
 */
const STORAGE_KEY = "lastPatientReportsContext";

const readCachedContext = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.patientUuid) {
      return parsed;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
};

const GlobalReportsModalHost = () => {
  const toast = useToast();
  const contextRef = useRef(readCachedContext());
  const [userRole, setUserRole] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentFolderPath, setCurrentFolderPath] = useState(
    contextRef.current?.reportsFolderPath || "",
  );
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [processingFiles] = useState({});

  // Role is needed so View Reports matches Schedules/PatientPage (e.g. Color Map
  // for admin/doctor only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const email = session?.user?.email;
        if (!email) return;
        const { data } = await supabase
          .from("users")
          .select("role")
          .eq("email", email)
          .maybeSingle();
        if (!cancelled && data?.role) setUserRole(data.role);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Track the last `patientReportsContext` payload so the bubble's open
  // request always reflects the freshest folder for the right patient.
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (!detail.patientUuid) return;
      contextRef.current = {
        patientUuid: detail.patientUuid,
        patientName: detail.patientName || "",
        reportsFolderPath: detail.reportsFolderPath || "",
      };
    };
    window.addEventListener("patientReportsContext", handler);
    return () =>
      window.removeEventListener("patientReportsContext", handler);
  }, []);

  const loadFolderFiles = useCallback(
    async (folderPath) => {
      if (!folderPath) {
        setCurrentFolderFiles([]);
        return;
      }
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase.storage
          .from(bucketName_files)
          .list(folderPath);
        if (error) {
          console.warn(
            "[GlobalReportsModalHost] Failed to list folder",
            folderPath,
            error,
          );
          setCurrentFolderFiles([]);
          return;
        }
        const filesOnly = (data || [])
          .filter((item) => item.name && item.name.includes("."))
          .map((file) => ({ ...file, actualFolderPath: folderPath }));
        setCurrentFolderFiles(filesOnly);
      } catch (err) {
        console.warn("[GlobalReportsModalHost] Error listing folder", err);
        setCurrentFolderFiles([]);
      }
    },
    [],
  );

  const refreshCurrentFolderFiles = useCallback(() => {
    if (currentFolderPath) {
      loadFolderFiles(currentFolderPath);
    }
  }, [currentFolderPath, loadFolderFiles]);

  // Open handler triggered by the floating Reports bubble when the user is
  // NOT on the matching patient page (the bubble dispatches this event then).
  useEffect(() => {
    const handler = async (e) => {
      const detail = e?.detail || {};
      const ctx = contextRef.current || {};
      const patientUuid = detail.patientUuid || ctx.patientUuid;
      if (!patientUuid) {
        toast({
          title: "No patient selected",
          description: "Open a patient first to view their reports.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const folderPath = detail.reportsFolderPath || ctx.reportsFolderPath || "";
      setCurrentFolderPath(folderPath);
      setModalIsOpen(true);
      await loadFolderFiles(folderPath);
    };
    window.addEventListener("globalOpenReportsModal", handler);
    return () =>
      window.removeEventListener("globalOpenReportsModal", handler);
  }, [loadFolderFiles, toast]);

  return (
    <ResultsTable
      globalModalOnly
      results={[]}
      prompts={[]}
      userRole={userRole}
      modalIsOpen={modalIsOpen}
      setModalIsOpen={setModalIsOpen}
      currentFolderPath={currentFolderPath}
      previousFolderPath=""
      currentFolderFiles={currentFolderFiles}
      refreshCurrentFolderFiles={refreshCurrentFolderFiles}
      handleViewFolderFiles={async (folderPath) => {
        setCurrentFolderPath(folderPath);
        await loadFolderFiles(folderPath);
      }}
      handleGoUpOneLevel={() => {}}
      handleGoDownOneLevel={() => {}}
      supabaseUrl={supabaseUrl}
      bucketName_files={bucketName_files}
      processingFiles={processingFiles}
    />
  );
};

export default GlobalReportsModalHost;
